import { Navigate, useParams } from "react-router-dom";
import Loader from "../components/Loader";
import { useGetMeQuery } from "../services/api";
import { MASTER_TOKEN_KEY } from "../services/masterApi";
import { isImpersonating } from "./impersonation";

/**
 * Handles the legacy bare "/:id" route which has been replaced by
 * "/dashboard/:id". If the visitor is logged in and genuine (valid token +
 * successful /me), send them to their dashboard; otherwise send them to login.
 */
const LegacyIdRedirect = () => {
  const { uniqueId } = useParams<{ uniqueId?: string }>();
  const token = localStorage.getItem("token");

  const isMasterUser =
    !!localStorage.getItem(MASTER_TOKEN_KEY) &&
    localStorage.getItem("userType") === "MASTER_USER";

  const {
    data: meData,
    isLoading,
    isError,
    isUninitialized,
  } = useGetMeQuery(undefined, { skip: !token });

  // Still resolving the session — show a loader.
  if (token && (isLoading || isUninitialized)) {
    return (
      <div className="h-dvh">
        <Loader isLoading={true} />
      </div>
    );
  }

  // Invalid/expired token — clear it so downstream pages don't see a stale session.
  if (token && isError) {
    localStorage.removeItem("token");
  }

  const sessionUser = meData?.data?.user;

  // If we're a master user and we're impersonating, redirect to the
  // impersonated user's dashboard.
  if (isMasterUser && isImpersonating()) {
    const matchesSession =
      !!uniqueId &&
      (uniqueId === sessionUser?.uniqueId ||
        uniqueId === sessionUser?.zohoCustomerId);

    if (!matchesSession) {
      return <Navigate to="/users" replace />;
    }

    return (
      <Navigate
        to={`/dashboard/${
          sessionUser?.uniqueId || sessionUser?.zohoCustomerId
        }`}
        replace
      />
    );
  }

  const isAuthenticated = !!token && !isError && !!meData?.data;

  if (isAuthenticated) {
    const targetId = sessionUser?.uniqueId || uniqueId;
    if (!targetId) {
      return <Navigate to="/login" replace />;
    }
    return <Navigate to={`/onboarding/${targetId}`} replace />;
  }

  return <Navigate to="/login" replace />;
};

export default LegacyIdRedirect;
